import React from "react";
import { Download, Loader2 } from "lucide-react";

const PURPLE = "#4b2a83";

const LOCATIONS = {
  metro: "Single residential property located in the Melbourne metropolitan area",
  regional: "Single residential property located outside the Melbourne metropolitan area",
};

const money = (n) => (n == null || n === "" ? "" : `$${Number(n).toLocaleString("en-AU")}`);
const dmy = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};
const longDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
};
const titleCase = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const na = (v) => (v === null || v === undefined || v === "" ? "N/A" : v);

const H2 = ({ children }) => (
  <h2 className="text-base font-bold mt-7 mb-2 pb-1 border-b" style={{ color: PURPLE, borderColor: PURPLE }}>{children}</h2>
);
const Row = ({ label, children, big }) => (
  <div className="flex gap-4 py-1 text-sm">
    <div className="w-48 flex-shrink-0 font-bold text-gray-800">{label}</div>
    <div className={big ? "text-base font-bold text-gray-900" : "text-gray-800"}>{children}</div>
  </div>
);
const Note = ({ children }) => <p className="text-xs text-gray-500 mb-2">{children}</p>;

/**
 * A published Property Price Statement, section for section as the statutory
 * form (s47AF Estate Agents Act 1980) and Property Data's public page.
 */
const PriceStatementPublic = ({ report, onDownload, downloading }) => {
  const st = report.statement || {};
  const median = st.median || {};
  const single = st.price_type === "single";
  const comps = (report.comparables || []).slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-3">
      <article className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-sm p-6 sm:p-10">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: PURPLE }}>Property Price Statement</h1>
            <p className="font-bold text-gray-900">{LOCATIONS[st.location] || LOCATIONS.metro}</p>
            <p className="text-xs text-gray-500 mt-1">Section 47AF of the Estate Agents Act 1980</p>
          </div>
          <button type="button" onClick={onDownload} disabled={downloading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            PDF
          </button>
        </div>

        <H2>Date prepared</H2>
        <p className="text-sm">{dmy(report.published_at || report.created_at)}</p>

        <H2>Property offered for sale</H2>
        <Row label={<>Address<br /><span className="text-xs font-normal text-gray-500">Including suburb and postcode</span></>} big>
          {report.full_address}
        </Row>

        <H2>Key features of the property</H2>
        <Row label="Building Type">{na(titleCase(report.property_type))}</Row>
        <Row label="# of bedrooms">{na(report.bedrooms)}</Row>
        <Row label="# of bathrooms">{na(report.bathrooms)}</Row>
        <Row label="# of car spaces">{na(report.car_spaces)}</Row>
        <Row label="Internal floor space (in m2)">{na(st.internal_floor_area)}</Row>
        <Row label="Total land size (in m2)">{report.land_size ? Math.round(report.land_size) : "N/A"}</Row>

        <H2>Indicative selling price</H2>
        <Note>
          For the meaning of indicative selling price, see consumer.vic.gov.au/underquoting. An indicative selling
          price may be a single price or a range of not more than 10% of the value of the lower limit.
        </Note>
        <Row label="Single price" big>{single ? money(report.sale_estimate_min) : ""}</Row>
        <Row label="or">{" "}</Row>
        <Row label="Range between" big>
          {!single && report.sale_estimate_min && report.sale_estimate_max
            ? `${money(report.sale_estimate_min)} & ${money(report.sale_estimate_max)}` : ""}
        </Row>

        <H2>Seller&apos;s reserve price (if property is to be sold by auction or fixed-date sale)</H2>
        <Note>
          For the meaning of seller&apos;s reserve price, see consumer.vic.gov.au/underquoting. When the estate agent or
          agent&apos;s representative receives the seller&apos;s reserve price it must be included in this section.
        </Note>
        <Row label="Seller's reserve price">{money(st.reserve_price) || "N/A"}</Row>

        <H2>Median sale price</H2>
        <p className="text-sm text-gray-800 mb-2">
          This section describes the median selling price for residential properties sold in the same suburb as the
          property offered for sale, sold in a 3-to-12-month period ending no more than 6 months before this statement
          was prepared.
        </p>
        <Row label="Median price">{money(median.price)}</Row>
        <Row label="Property type">{titleCase(median.property_type)}</Row>
        <Row label="Suburb">{median.suburb || report.suburb}</Row>
        <Row label="Period: from">{dmy(median.period_from)}</Row>
        <Row label="to">{dmy(median.period_to)}</Row>
        <Row label="Source">{median.source}</Row>

        <H2>Comparable property sales</H2>
        <p className="text-sm text-gray-800 mb-2">
          These are the three most comparable properties to the property for sale that were sold within two kilometres
          of the property for sale in the last six months.
        </p>
        <p className="text-sm text-gray-800 mb-2">
          If there were fewer than three, but one or two, comparable properties, these must be included below.
        </p>
        <p className="text-sm text-gray-800 mb-3">These properties were used to help determine the estimated selling price.</p>
        {comps.map((c, i) => (
          <div key={c.id || i} className="mb-4">
            <h3 className="text-sm font-bold text-gray-900 mb-1">Comparable property {i + 1}</h3>
            <Row label="Address">{c.full_address}</Row>
            <Row label="Sale price">{money(c.price) || c.price_label || "N/A"}</Row>
            <Row label="Date of sale">{dmy(c.sold_date) || "N/A"}</Row>
            <p className="text-xs text-gray-500 mt-1">Key features</p>
            <Row label="Building Type">{na(titleCase(c.property_type))}</Row>
            <Row label="# of bedrooms">{na(c.bedrooms)}</Row>
            <Row label="# of bathrooms">{na(c.bathrooms)}</Row>
            <Row label="# of car spaces">{na(c.car_spaces)}</Row>
            <Row label="Total land size (in m2)">{c.land_size ? Math.round(c.land_size) : "N/A"}</Row>
          </div>
        ))}

        <H2>Reason there are fewer than three comparable properties</H2>
        <p className="text-sm text-gray-800 mb-2">
          I, the agent or agent&apos;s representative for the property offered for sale, did not take the sale price of
          three comparable properties into account in determining the estimated selling price for the property because
          I believe that fewer than three comparable properties were sold within the relevant period.
        </p>
        <p className="text-sm text-gray-800 mb-1">
          Explain the reason the agent or agent&apos;s representative believes that fewer than three comparable properties:
        </p>
        <p className="text-sm italic text-gray-800">{st.fewer_reason || "N/A"}</p>

        <H2>Sale outcome</H2>
        <Note>
          The sale price of the property must be included in this section within seven days after the unconditional
          sale of the property unless an application for an exemption is pending or an exemption has been granted.
        </Note>
        <Row label="Sale price">{money(st.sold_price)}</Row>
        <Row label="Unconditional sale date">{dmy(st.sold_date)}</Row>

        <footer className="mt-8 pt-3 border-t border-gray-200 text-xs text-gray-500 space-y-1">
          <p>This statement was last updated {longDate(report.updated_at || report.published_at)}.</p>
          <p>
            This Property Price Statement is publicly available for at least 18 months from the sale, unless an
            exemption has been granted by the Director of Consumer Affairs Victoria.
          </p>
          {report.agent?.name && <p>Prepared by {report.agent.name}, Collings Real Estate.</p>}
        </footer>
      </article>
    </div>
  );
};

export default PriceStatementPublic;
